// src/pages/DecisionDashboard.tsx

import { useEffect, useMemo, useState } from 'react';
import { useApi } from '../utils/useApi';

import Alert from 'react-bootstrap/Alert';
// import Container from 'react-bootstrap/Container';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import Stack from 'react-bootstrap/Stack';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import React from 'react';

type DecisionDashboardRow = {
  decision_id: number;
  client_name: string;
  file_number: string | null;
  subject: string | null;
  number_of_decision_files: number;
  files?: DecisionFile[];
  // responsible_admin: string | null;
  // status: string | null;
};

type DecisionFile = {
  id: number;
  decision_id: number;
  filename: string;
  description?: string | null;
  original_filename?: string | null;
  file_name?: string | null;
  upload_date?: string | null;
  uploaded_by?: string | null;
  responsible_admin: string | null;
  responsible_admin_id: number | null;
  status: string | null;
  status_id: number | null;
};

const DecisionDashboard = () => {
  const { apiFetch } = useApi();

  const [rows, setRows] = useState<DecisionDashboardRow[]>([]);
  const [decisionFiles, setDecisionFiles] = useState<DecisionFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState<keyof DecisionDashboardRow>('client_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedFile, setSelectedFile] = useState<DecisionFile | null>(null);
  const [fileSaving, setFileSaving] = useState(false);
  // const [files, setFiles] = useState<DecisionFile[]>([]);

  const openFileModal = (file: DecisionFile) => {
    setSelectedFile(file);
  };

  const closeFileModal = () => {
    setSelectedFile(null);
  };

  const filesByDecisionId = useMemo(() => {
    return decisionFiles.reduce<Record<number, DecisionFile[]>>((acc, file) => {
      if (!acc[file.decision_id]) acc[file.decision_id] = [];
      acc[file.decision_id].push(file);
      return acc;
    }, {});
  }, [decisionFiles]);

  const toggleSort = (field: keyof DecisionDashboardRow) => {
    if (sortField === field) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortField(field);
    setSortDirection('asc');
  };

  const sortIcon = (field: keyof DecisionDashboardRow) => {
    if (sortField !== field) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const getFileLabel = (file: DecisionFile) => {
    return file.original_filename ?? file.filename ?? file.file_name ?? `Datei ${file.id}`;
  };

  const filteredAndSortedDecisions = rows
    .filter((row) => {
      const search = filter.toLowerCase();

      row.files = filesByDecisionId[row.decision_id] ?? [];

      return [
        row.file_number,
        row.client_name,
        row.subject,
        row.number_of_decision_files,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search));
    })
    .sort((a, b) => {
      const aValue = a[sortField] ?? '';
      const bValue = b[sortField] ?? '';

      const result = String(aValue).localeCompare(String(bValue), 'de', {
        numeric: true,
        sensitivity: 'base',
      });

      return sortDirection === 'asc' ? result : -result;
    });

  // const getFiles = (row: DecisionDashboardRow) => {
  //   setFiles(filesByDecisionId[row.decision_id] ?? []);
  //   return true;
  // };

  const updateSelectedFile = async (payload: Partial<DecisionFile>) => {
    if (!selectedFile) return;

    setFileSaving(true);

    try {
      const updatedFile = await apiFetch(
        `/api/decision-dashboard/file/${selectedFile.id}`,
        'PATCH',
        payload
      );

      setDecisionFiles((current) =>
        current.map((file) => (file.id === updatedFile.id ? updatedFile : file))
      );

      setSelectedFile(updatedFile);
    } catch {
      setError('Datei konnte nicht aktualisiert werden.');
    } finally {
      setFileSaving(false);
    }
  };

  const assignCurrentUser = () => {
    if (!selectedFile) return;

    updateSelectedFile({
      responsible_admin_id: null,
    });
  };

  const downloadSelectedFile = async () => {
    if (!selectedFile) return;

    const blob = await apiFetch(
      `/api/decision-dashboard/file/${selectedFile.id}/download`,
      'GET',
      undefined,
      true,
      'blob'
    );

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = getFileLabel(selectedFile);
    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    Promise.all([
      apiFetch('/api/decision-dashboard'),
      apiFetch('/api/decisions/files'),
    ])
      .then(([dashboardRows, files]) => {
        setRows(dashboardRows);
        setDecisionFiles(files);
      })
      .catch(() => setError('Dashboard konnte nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, [apiFetch]);

{/* 
//#region HTML
*/}
  return (
    <>
      {loading && <Spinner animation="border" />}
      {error && <Alert variant="danger">{error}</Alert>}
      {!loading && !error && (
        <div className="container mt-4">
          <Stack direction="horizontal" gap={3} className="mb-3">
              <h2 className="mb-0">Bescheide mit hochgeladenen Dateien</h2>
              {error && <Alert variant="danger">{error}</Alert>}
          </Stack>
          <Stack direction="horizontal" gap={0}>
              <Form.Control
                type="search"
                placeholder="Filtern nach Mandant, Aktenzeichen, Inhalt"
                className="p-2"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
          </Stack>
          <div className="text-muted small my-2">
            {filteredAndSortedDecisions.length} von {rows.length} Bescheiden
          </div>



          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th role="button" onClick={() => toggleSort('client_name')}>
                  Mandant&nbsp;{sortIcon('client_name')}
                </th>
                <th role="button" onClick={() => toggleSort('file_number')}>
                  Aktenzeichen&nbsp;{sortIcon('file_number')}
                </th>
                <th role="button" onClick={() => toggleSort('subject')}>
                  Inhalt&nbsp;{sortIcon('subject')}
                </th>
                <th role="button" onClick={() => toggleSort('number_of_decision_files')}>
                  Dateien&nbsp;{sortIcon('number_of_decision_files')}
                </th>
                <th>Verantwortlich</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
            {filteredAndSortedDecisions.map((row: DecisionDashboardRow) => (
                <React.Fragment key={row.decision_id}>
                  <tr>
                    <td>{row.client_name}</td>
                    <td>{row.file_number ?? '—'}</td>
                    <td>{row.subject ?? '—'}</td>
                    <td>{row.files?.length ?? 0}</td>
                    <td/>
                    <td/>
                  </tr>

                  {row.files && row.files.length > 0 && row.files.map((file: DecisionFile) => (
                    <tr key={file.id} className="bg-light">
                      <td />
                      <td>{file.description}</td>
                      <td colSpan={2} className="bg-light" onClick={() => openFileModal(file)} style={{ cursor: 'pointer' }}>
                        {getFileLabel(file)}
                      </td>
                      <td>{file.responsible_admin ?? '—'}</td>
                      <td className={`status-${file.status_id}`}>{file.status ?? '—'}</td>
                    </tr>
                  ))}
                </React.Fragment>
              )
            )}
            </tbody>
          </Table>
        </div>
      )}
      <Modal show={selectedFile !== null} onHide={closeFileModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Dateidetails</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedFile && (
            <>
              <dl className="row mb-3">
                <dt className="col-sm-4">Dateiname</dt>
                <dd className="col-sm-8">{getFileLabel(selectedFile)}</dd>

                <dt className="col-sm-4">Hochgeladen am</dt>
                <dd className="col-sm-8">{selectedFile.upload_date ?? '—'} durch {selectedFile.uploaded_by ?? '—'}</dd>

                <dt className="col-sm-4">Verantwortlich</dt>
                <dd className="col-sm-8">{selectedFile.responsible_admin ?? '—'}</dd>
              </dl>

              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={selectedFile.status_id ?? 0}
                  disabled={fileSaving}
                  onChange={(e) =>
                    updateSelectedFile({
                      status_id: Number(e.target.value),
                    })
                  }
                >
                  <option value={0}>Neu</option>
                  <option value={1}>In Arbeit</option>
                  <option value={2}>Erledigt</option>
                </Form.Select>
              </Form.Group>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeFileModal}>
            Schließen
          </Button>

          <Button
            variant="outline-primary"
            disabled={!selectedFile || fileSaving}
            onClick={assignCurrentUser}
          >
            Mir zuweisen
          </Button>

          <Button
            variant="primary"
            disabled={!selectedFile}
            onClick={downloadSelectedFile}
          >
            Herunterladen
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DecisionDashboard;